import { useCallback, useRef } from 'react';

import { _2dMapCombinedQuote } from '@larki/shared/type/order.v2';

import { useLazyGetCombinedQuoteForVersionQuery } from '../redux/rtk';

export type CombinedQuoteMode = 'combined' | 'live';

type OnCombinedQuote = (args: {
  jobId: number;
  quote: _2dMapCombinedQuote;
}) => void;

export function useCombinedQuoteMode({
  onCombinedQuote,
}: {
  onCombinedQuote: OnCombinedQuote;
}) {
  const [getCombinedQuoteForVersion] = useLazyGetCombinedQuoteForVersionQuery();
  const modeRef = useRef<Map<number, CombinedQuoteMode>>(
    new Map<number, CombinedQuoteMode>()
  );

  const reset = useCallback((versionId?: number | null) => {
    if (!versionId) return;
    modeRef.current.delete(versionId);
  }, []);

  const getCachedMode = useCallback(
    (versionId: number) => modeRef.current.get(versionId),
    []
  );

  /**
   * Fetch combined quote and apply it via `onCombinedQuote`.
   * Returns true if a combined quote was successfully fetched.
   
   */
  const fetchAndApplyCombined = useCallback(
    async (versionId: number, jobId: number): Promise<boolean> => {
      try {
        const quote = await getCombinedQuoteForVersion({ versionId }).unwrap();
        modeRef.current.set(versionId, 'combined');
        onCombinedQuote({ jobId, quote });
        return true;
      } catch {
        return false;
      }
    },
    [getCombinedQuoteForVersion, onCombinedQuote]
  );

  /**
   * Determine (and cache) mode for a versionId once. If a combined quote exists,
   * it will be fetched and applied as part of detection.
   */
  const ensureMode = useCallback(
    async (versionId: number, jobId: number): Promise<CombinedQuoteMode> => {
      const cached = modeRef.current.get(versionId);
      if (cached) return cached;

      const hasCombined = await fetchAndApplyCombined(versionId, jobId);
      const mode: CombinedQuoteMode = hasCombined ? 'combined' : 'live';
      modeRef.current.set(versionId, mode);
      return mode;
    },
    [fetchAndApplyCombined]
  );

  return { ensureMode, fetchAndApplyCombined, reset, getCachedMode };
}
